const TRASPORTO = { mittente: "Mittente", destinatario: "Destinatario", vettore: "Vettore" };
const PORTO = { franco: "Franco", assegnato: "Assegnato" };

const itDate = (iso, time = false) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const opts = { day: "2-digit", month: "2-digit", year: "numeric", ...(time ? { hour: "2-digit", minute: "2-digit" } : {}) };
  return d.toLocaleString("it-IT", opts);
};

const Box = ({ label, children, className = "" }) => (
  <div className={`border border-slate-300 px-2.5 py-1.5 ${className}`}>
    <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
    <div className="text-xs mt-0.5 min-h-[16px] whitespace-pre-line">{children || "—"}</div>
  </div>
);

const Firma = ({ label }) => <div className="border-t border-slate-400 pt-1 text-[10px] text-slate-600 text-center">{label}</div>;

// Documento di trasporto (D.P.R. 472/1996) stampabile/esportabile in PDF.
export default function DdtPDF({ ddt, settings, domId = "ddt-print-area" }) {
  if (!ddt) return null;
  const accent = settings?.accent_color || "#E1352A";
  const reso = ddt.tipo === "reso";
  return (
    <div id={domId} data-no-translate className="bg-white text-slate-900 mx-auto" style={{ maxWidth: 820, padding: 36 }}>
      <div className="flex justify-between items-start gap-6 pb-4 mb-4" style={{ borderBottom: `3px solid ${accent}` }}>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Mittente</div>
          <div className="font-heading text-lg font-extrabold uppercase tracking-tight" style={{ color: accent }}>{settings?.ragione_sociale || ""}</div>
          <div className="text-xs text-slate-500 mt-1 leading-relaxed">
            {settings?.indirizzo && <div>{settings.indirizzo}</div>}
            {settings?.piva && <div>P.IVA {settings.piva}</div>}
            {settings?.telefono && <div>Tel. {settings.telefono}</div>}
            {settings?.email && <div>{settings.email}</div>}
          </div>
        </div>
        <div className="text-right shrink-0">
          {settings?.logo_url && <img src={settings.logo_url} alt="logo" crossOrigin="anonymous" style={{ maxHeight: 54, maxWidth: 170, marginLeft: "auto", marginBottom: 8, objectFit: "contain" }} />}
          <div className="font-heading text-xl font-extrabold leading-tight" style={{ color: accent }}>DOCUMENTO DI TRASPORTO</div>
          <div className="text-[10px] text-slate-500">D.D.T. ai sensi del D.P.R. 472/1996{reso ? " · RESO" : ""}</div>
          <div className="text-sm font-semibold mt-1" data-testid="ddt-pdf-number">N. {ddt.numero || "—"}</div>
          <div className="text-xs text-slate-500">Data: {itDate(ddt.data)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-0 mb-3">
        <Box label={reso ? "Destinatario del reso" : "Destinatario (cessionario)"}>
          <span className="font-semibold text-sm">{ddt.destinatario_nome}</span>
          {ddt.destinatario_indirizzo ? `\n${ddt.destinatario_indirizzo}` : ""}
          {ddt.destinatario_piva ? `\nP.IVA / C.F. ${ddt.destinatario_piva}` : ""}
        </Box>
        <Box label="Luogo di destinazione" className="border-l-0">{ddt.luogo_destinazione || "Come indirizzo del destinatario"}</Box>
      </div>

      <div className="grid grid-cols-4 mb-3">
        <Box label="Causale del trasporto">{ddt.causale}</Box>
        <Box label="Trasporto a cura del" className="border-l-0">{TRASPORTO[ddt.trasporto_a_cura]}</Box>
        <Box label="Porto" className="border-l-0">{PORTO[ddt.porto]}</Box>
        <Box label="Rif. preventivo" className="border-l-0">{ddt.quote_numero ? `N. ${ddt.quote_numero}` : ""}</Box>
      </div>
      {ddt.trasporto_a_cura === "vettore" && <Box label="Vettore (ragione sociale, indirizzo, P.IVA)" className="mb-3">{ddt.vettore}</Box>}

      <table className="w-full text-xs mb-3" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: accent, color: "#fff" }}>
            <th className="text-left py-2 px-2 font-semibold" style={{ width: 70 }}>Cod.</th>
            <th className="text-left py-2 px-2 font-semibold">Descrizione dei beni (natura e qualità)</th>
            <th className="text-center py-2 px-2 font-semibold" style={{ width: 70 }}>U.M.</th>
            <th className="text-right py-2 px-2 font-semibold" style={{ width: 80 }}>Quantità</th>
          </tr>
        </thead>
        <tbody>
          {(ddt.righe || []).map((r, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #e2e8f0" }}>
              <td className="py-1.5 px-2 text-slate-500">{r.codice || "—"}</td>
              <td className="py-1.5 px-2 whitespace-pre-line">{r.descrizione}</td>
              <td className="py-1.5 px-2 text-center">{r.unita}</td>
              <td className="py-1.5 px-2 text-right font-medium">{r.quantita}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid grid-cols-4 mb-3">
        <Box label="Aspetto esteriore dei beni">{ddt.aspetto_beni}</Box>
        <Box label="N. colli" className="border-l-0">{ddt.colli != null ? String(ddt.colli) : ""}</Box>
        <Box label="Peso (kg)" className="border-l-0">{ddt.peso_kg != null ? String(ddt.peso_kg) : ""}</Box>
        <Box label="Data e ora inizio trasporto" className="border-l-0">{itDate(ddt.data_ritiro, true)}</Box>
      </div>
      {ddt.note && <Box label="Annotazioni" className="mb-3">{ddt.note}</Box>}

      <div className="grid grid-cols-3 gap-8 pt-12">
        <Firma label="Firma del conducente" />
        <Firma label="Firma del vettore" />
        <Firma label="Firma del destinatario" />
      </div>
      <div className="text-center text-[10px] text-slate-400 mt-8">{settings?.ragione_sociale ? `Gestionale di ${settings.ragione_sociale}` : "K Gestionale"}</div>
    </div>
  );
}
